import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, Phone, MapPin, Building2, FileText, Shield, Menu, X } from 'lucide-react';
import { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const LOGO_URL = "/logo.png";

// Informations de contact
const RESAWORK_INFO = {
  email: 'support@resawork.fr',
  phone: '04 84 49 27 00',
  address: '25 Av du Tubé, 13800 ISTRES'
};

const MSC_INFO = {
  name: 'Agence MSC - Mon Service Com',
  address: '52 rue des Pétouses, 13800 ISTRES',
  siret: '123 456 789 00012', // À remplacer par le vrai SIRET
};

// Header commun pour les pages légales
function LegalHeader({ title }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src={LOGO_URL} alt="ResaWork" className="h-16" />
            </Link>
            
            <nav className="hidden md:flex items-center gap-8">
              <Link to="/docs" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">Documentation</Link>
              <Link to="/changelog" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">Changelog</Link>
              <Link to="/tickets" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">Support</Link>
            </nav>
            
            <div className="hidden md:flex items-center gap-3">
              <Button variant="ghost" asChild>
                <Link to="/login">Connexion</Link>
              </Button>
              <Button asChild className="rounded-full">
                <Link to="/onboarding">Démarrer</Link>
              </Button>
            </div>
            
            <button className="md:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </header>

      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 bg-background pt-16 md:hidden">
          <nav className="flex flex-col p-4 gap-4">
            <Link to="/docs" className="text-lg font-medium" onClick={() => setMobileMenuOpen(false)}>Documentation</Link>
            <Link to="/changelog" className="text-lg font-medium" onClick={() => setMobileMenuOpen(false)}>Changelog</Link>
            <Link to="/tickets" className="text-lg font-medium" onClick={() => setMobileMenuOpen(false)}>Support</Link>
            <hr className="my-2" />
            <Link to="/login" className="text-lg font-medium" onClick={() => setMobileMenuOpen(false)}>Connexion</Link>
            <Button asChild className="rounded-full mt-2">
              <Link to="/onboarding" onClick={() => setMobileMenuOpen(false)}>Démarrer</Link>
            </Button>
          </nav>
        </div>
      )}
    </>
  );
}

// Footer commun
function LegalFooter() {
  return (
    <footer className="bg-slate-900 text-white py-12 px-4">
      <div className="container mx-auto max-w-6xl">
        <div className="grid md:grid-cols-4 gap-8 mb-8">
          <div>
            <img src={LOGO_URL} alt="ResaWork" className="h-12 mb-4 brightness-0 invert" />
            <p className="text-slate-400 text-sm">
              Solution de gestion de réservations en ligne pour les entreprises de services.
            </p>
          </div>
          
          <div>
            <h4 className="font-semibold mb-4">Produit</h4>
            <ul className="space-y-2 text-sm text-slate-400">
              <li><Link to="/#fonctionnalites" className="hover:text-white transition-colors">Fonctionnalités</Link></li>
              <li><Link to="/#tarifs" className="hover:text-white transition-colors">Tarifs</Link></li>
              <li><Link to="/docs" className="hover:text-white transition-colors">Documentation</Link></li>
              <li><Link to="/changelog" className="hover:text-white transition-colors">Changelog</Link></li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-semibold mb-4">Support</h4>
            <ul className="space-y-2 text-sm text-slate-400">
              <li><Link to="/tickets" className="hover:text-white transition-colors">Centre d'aide</Link></li>
              <li><a href={`mailto:${RESAWORK_INFO.email}`} className="hover:text-white transition-colors">{RESAWORK_INFO.email}</a></li>
              <li><a href={`tel:${RESAWORK_INFO.phone.replace(/\s/g, '')}`} className="hover:text-white transition-colors">{RESAWORK_INFO.phone}</a></li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-semibold mb-4">Légal</h4>
            <ul className="space-y-2 text-sm text-slate-400">
              <li><Link to="/mentions-legales" className="hover:text-white transition-colors">Mentions légales</Link></li>
              <li><Link to="/confidentialite" className="hover:text-white transition-colors">Confidentialité</Link></li>
              <li><Link to="/cgu" className="hover:text-white transition-colors">CGU</Link></li>
            </ul>
          </div>
        </div>
        
        <div className="border-t border-slate-800 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm text-slate-400">
            © {new Date().getFullYear()} ResaWork - Un outil de l'Agence MSC - Mon Service Com. Tous droits réservés.
          </p>
          <p className="text-sm text-slate-500">
            {RESAWORK_INFO.address}
          </p>
        </div>
      </div>
    </footer>
  );
}

// Mentions Légales
export function MentionsLegales() {
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <LegalHeader title="Mentions Légales" />
      
      <main className="flex-1 pt-20 pb-12">
        <div className="container mx-auto px-4 max-w-4xl">
          <Link to="/" className="inline-flex items-center text-muted-foreground hover:text-foreground mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" /> Retour à l'accueil
          </Link>
          
          <h1 className="text-4xl font-heading font-bold mb-8">Mentions Légales</h1>
          
          <div className="prose prose-neutral dark:prose-invert max-w-none space-y-8">
            <section className="p-6 bg-muted/30 rounded-xl">
              <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
                <Building2 className="w-6 h-6 text-primary" />
                Éditeur du site
              </h2>
              <p className="mb-4">
                Le site <strong>ResaWork</strong> est un outil édité et développé par :
              </p>
              <div className="bg-background p-4 rounded-lg border">
                <p className="font-semibold text-lg mb-2">{MSC_INFO.name}</p>
                <ul className="list-none space-y-1 text-muted-foreground">
                  <li><strong>Siège social :</strong> {MSC_INFO.address}</li>
                  <li><strong>SIRET :</strong> {MSC_INFO.siret}</li>
                  <li><strong>Email :</strong> <a href={`mailto:${RESAWORK_INFO.email}`} className="text-primary hover:underline">{RESAWORK_INFO.email}</a></li>
                  <li><strong>Téléphone :</strong> <a href={`tel:${RESAWORK_INFO.phone.replace(/\s/g, '')}`} className="text-primary hover:underline">{RESAWORK_INFO.phone}</a></li>
                </ul>
              </div>
            </section>

            <section className="p-6 bg-muted/30 rounded-xl">
              <h2 className="text-2xl font-semibold mb-4">Adresse de ResaWork</h2>
              <div className="bg-background p-4 rounded-lg border">
                <p className="font-semibold mb-2">ResaWork</p>
                <ul className="list-none space-y-1 text-muted-foreground">
                  <li><strong>Adresse :</strong> {RESAWORK_INFO.address}</li>
                  <li><strong>Email :</strong> <a href={`mailto:${RESAWORK_INFO.email}`} className="text-primary hover:underline">{RESAWORK_INFO.email}</a></li>
                  <li><strong>Téléphone :</strong> <a href={`tel:${RESAWORK_INFO.phone.replace(/\s/g, '')}`} className="text-primary hover:underline">{RESAWORK_INFO.phone}</a></li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">Directeur de la publication</h2>
              <p>Le directeur de la publication est le représentant légal de l'Agence MSC - Mon Service Com.</p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">Hébergement</h2>
              <p>Le site est hébergé par :</p>
              <ul className="list-none space-y-1 mt-2">
                <li><strong>Agence MSC - Mon Service Com</strong></li>
                <li>Hébergement cloud sécurisé</li>
                <li>Contact : contact@agence-msc.fr</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">Propriété intellectuelle</h2>
              <p>
                L'ensemble du contenu de ce site (textes, images, logos, vidéos, logiciel) est la propriété exclusive 
                de l'Agence MSC - Mon Service Com ou de ses partenaires et est protégé par le droit d'auteur.
              </p>
              <p className="mt-2">
                Toute reproduction, représentation, modification, publication, transmission ou dénaturation, 
                totale ou partielle, du contenu de ce site est strictement interdite sans l'autorisation 
                expresse écrite de l'Agence MSC - Mon Service Com.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">Données personnelles</h2>
              <p>
                Les informations collectées sur ce site sont destinées à l'Agence MSC - Mon Service Com 
                pour la gestion des demandes, la fourniture du service ResaWork et le suivi de la relation commerciale.
              </p>
              <p className="mt-2">
                Conformément au Règlement Général sur la Protection des Données (RGPD), vous disposez d'un droit 
                d'accès, de rectification, de portabilité et de suppression de vos données personnelles.
              </p>
              <p className="mt-2">
                Pour exercer ces droits, contactez-nous à : <a href={`mailto:${RESAWORK_INFO.email}`} className="text-primary hover:underline">{RESAWORK_INFO.email}</a>
              </p>
              <p className="mt-2">
                Pour plus de détails, consultez notre <Link to="/confidentialite" className="text-primary hover:underline">Politique de Confidentialité</Link>.
              </p>
            </section>
          </div>
        </div>
      </main>
      
      <LegalFooter />
    </div>
  );
}

// Politique de Confidentialité
export function PolitiqueConfidentialite() {
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <LegalHeader title="Politique de Confidentialité" />
      
      <main className="flex-1 pt-20 pb-12">
        <div className="container mx-auto px-4 max-w-4xl">
          <Link to="/" className="inline-flex items-center text-muted-foreground hover:text-foreground mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" /> Retour à l'accueil
          </Link>
          
          <h1 className="text-4xl font-heading font-bold mb-8">Politique de Confidentialité</h1>
          
          <div className="prose prose-neutral dark:prose-invert max-w-none space-y-8">
            <section className="p-6 bg-primary/5 rounded-xl border border-primary/20">
              <p className="text-muted-foreground">
                <strong>ResaWork</strong> est un outil développé par l'<strong>Agence MSC - Mon Service Com</strong>, 
                dont le siège social est situé au <strong>52 rue des Pétouses, 13800 ISTRES</strong>.
              </p>
              <p className="text-muted-foreground mt-2">
                Cette politique de confidentialité décrit comment nous collectons, utilisons et protégeons vos données personnelles.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
                <Shield className="w-6 h-6 text-primary" />
                1. Responsable du traitement
              </h2>
              <p>Le responsable du traitement des données personnelles est :</p>
              <div className="bg-muted/30 p-4 rounded-lg mt-2">
                <p><strong>{MSC_INFO.name}</strong></p>
                <p className="text-muted-foreground">{MSC_INFO.address}</p>
                <p className="text-muted-foreground">Email : {RESAWORK_INFO.email}</p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">2. Données collectées</h2>
              <p>Nous collectons les données suivantes :</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li><strong>Données d'identification :</strong> nom, prénom, email, téléphone, adresse</li>
                <li><strong>Données de connexion :</strong> logs de connexion, adresse IP, navigateur utilisé</li>
                <li><strong>Données professionnelles :</strong> nom de l'entreprise, SIRET, activité</li>
                <li><strong>Données relatives aux réservations :</strong> historique des réservations, préférences</li>
                <li><strong>Données de paiement :</strong> historique des transactions (les données bancaires sont traitées par Stripe)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">3. Finalités du traitement</h2>
              <p>Vos données sont utilisées pour :</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>La création et la gestion de votre compte utilisateur</li>
                <li>La fourniture du service ResaWork (réservations, planning, facturation)</li>
                <li>L'envoi de notifications relatives à votre compte et vos réservations</li>
                <li>L'amélioration de nos services et de l'expérience utilisateur</li>
                <li>Le respect de nos obligations légales et fiscales</li>
                <li>La gestion de la relation commerciale et du support client</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">4. Base légale du traitement</h2>
              <p>Le traitement de vos données repose sur :</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li><strong>L'exécution du contrat :</strong> pour la fourniture du service ResaWork</li>
                <li><strong>Le consentement :</strong> pour l'envoi de communications marketing (si applicable)</li>
                <li><strong>L'intérêt légitime :</strong> pour l'amélioration de nos services</li>
                <li><strong>L'obligation légale :</strong> pour la conservation des factures et données fiscales</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">5. Durée de conservation</h2>
              <p>Vos données sont conservées :</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li><strong>Données de compte :</strong> pendant toute la durée de votre utilisation du service, puis 3 ans après la fin de la relation commerciale</li>
                <li><strong>Données de facturation :</strong> 10 ans conformément aux obligations légales</li>
                <li><strong>Logs de connexion :</strong> 1 an</li>
                <li><strong>Cookies :</strong> 13 mois maximum</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">6. Vos droits</h2>
              <p>Conformément au RGPD, vous disposez des droits suivants :</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li><strong>Droit d'accès :</strong> obtenir une copie de vos données personnelles</li>
                <li><strong>Droit de rectification :</strong> corriger des données inexactes</li>
                <li><strong>Droit à l'effacement :</strong> demander la suppression de vos données</li>
                <li><strong>Droit à la portabilité :</strong> recevoir vos données dans un format structuré</li>
                <li><strong>Droit d'opposition :</strong> vous opposer à certains traitements</li>
                <li><strong>Droit à la limitation :</strong> restreindre le traitement de vos données</li>
              </ul>
              <p className="mt-4">
                Pour exercer ces droits, contactez-nous à : <a href={`mailto:${RESAWORK_INFO.email}`} className="text-primary hover:underline">{RESAWORK_INFO.email}</a>
              </p>
              <p className="mt-2">
                Vous pouvez également introduire une réclamation auprès de la CNIL (www.cnil.fr).
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">7. Cookies</h2>
              <p>
                Nous utilisons des cookies essentiels au fonctionnement du site (authentification, session, préférences).
                Aucun cookie publicitaire ou de tracking tiers n'est utilisé.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">8. Sécurité</h2>
              <p>
                Nous mettons en œuvre des mesures techniques et organisationnelles appropriées pour protéger vos données 
                contre tout accès non autorisé, modification, divulgation ou destruction. Les données sont chiffrées 
                en transit (HTTPS) et au repos.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">9. Contact</h2>
              <div className="bg-muted/30 p-4 rounded-lg">
                <p>Pour toute question concernant cette politique de confidentialité :</p>
                <ul className="list-none space-y-1 mt-2">
                  <li><strong>Email :</strong> <a href={`mailto:${RESAWORK_INFO.email}`} className="text-primary hover:underline">{RESAWORK_INFO.email}</a></li>
                  <li><strong>Téléphone :</strong> {RESAWORK_INFO.phone}</li>
                  <li><strong>Adresse :</strong> {RESAWORK_INFO.address}</li>
                </ul>
              </div>
            </section>
            
            <p className="text-sm text-muted-foreground">
              Dernière mise à jour : {new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
          </div>
        </div>
      </main>
      
      <LegalFooter />
    </div>
  );
}

// CGU
export function CGU() {
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <LegalHeader title="Conditions Générales d'Utilisation" />
      
      <main className="flex-1 pt-20 pb-12">
        <div className="container mx-auto px-4 max-w-4xl">
          <Link to="/" className="inline-flex items-center text-muted-foreground hover:text-foreground mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" /> Retour à l'accueil
          </Link>
          
          <h1 className="text-4xl font-heading font-bold mb-8">Conditions Générales d'Utilisation</h1>
          
          <div className="prose prose-neutral dark:prose-invert max-w-none space-y-8">
            <section className="p-6 bg-primary/5 rounded-xl border border-primary/20">
              <p className="text-muted-foreground">
                <strong>ResaWork</strong> est un outil de gestion de réservations en ligne édité par 
                l'<strong>Agence MSC - Mon Service Com</strong>, dont le siège social est situé au 
                <strong> 52 rue des Pétouses, 13800 ISTRES</strong>.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
                <FileText className="w-6 h-6 text-primary" />
                Article 1 - Objet
              </h2>
              <p>
                Les présentes Conditions Générales d'Utilisation (CGU) régissent l'accès et l'utilisation 
                de la plateforme ResaWork, solution SaaS de gestion de réservations en ligne destinée 
                aux entreprises de services.
              </p>
              <p className="mt-2">
                L'utilisation du service implique l'acceptation pleine et entière des présentes CGU.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">Article 2 - Description du service</h2>
              <p>ResaWork propose les fonctionnalités suivantes :</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Création et gestion de widgets de réservation intégrables</li>
                <li>Gestion du planning et des disponibilités</li>
                <li>Gestion des clients et CRM intégré</li>
                <li>Gestion des collaborateurs et de leurs plannings</li>
                <li>Système de facturation et suivi des paiements</li>
                <li>Page de connexion dédiée pour les clients de l'entreprise</li>
                <li>Notifications et rappels automatiques</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">Article 3 - Accès au service</h2>
              <p>
                L'accès à ResaWork nécessite la création d'un compte. L'utilisateur s'engage à fournir 
                des informations exactes et complètes lors de l'inscription et à les maintenir à jour.
              </p>
              <p className="mt-2">
                L'utilisateur est seul responsable de la confidentialité de ses identifiants de connexion 
                et de toute activité réalisée depuis son compte.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">Article 4 - Période d'essai</h2>
              <ul className="list-disc pl-6 space-y-1">
                <li>Une période d'essai gratuite de <strong>15 jours</strong> est proposée à tout nouvel utilisateur</li>
                <li>Pendant l'essai, l'accès est limité à <strong>3 clients</strong> maximum</li>
                <li>Toutes les fonctionnalités sont disponibles pendant la période d'essai</li>
                <li>Aucune carte bancaire n'est requise pour l'essai</li>
                <li>À l'issue de la période d'essai, un abonnement payant est nécessaire pour continuer à utiliser le service</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">Article 5 - Abonnement et paiement</h2>
              <ul className="list-disc pl-6 space-y-1">
                <li>Deux formules d'abonnement sont proposées : mensuelle et annuelle</li>
                <li>Les tarifs en vigueur sont affichés sur le site et peuvent être modifiés avec un préavis de 30 jours</li>
                <li>L'abonnement est renouvelé automatiquement à chaque échéance</li>
                <li>Le paiement s'effectue par carte bancaire via Stripe, plateforme de paiement sécurisée</li>
                <li>En cas de non-paiement, l'accès au back-office est suspendu</li>
                <li>Le widget de réservation reste actif pour ne pas pénaliser les clients finaux</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">Article 6 - Obligations de l'utilisateur</h2>
              <p>L'utilisateur s'engage à :</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Utiliser le service conformément à sa destination et dans le respect de la législation en vigueur</li>
                <li>Ne pas porter atteinte à la sécurité ou à l'intégrité du service</li>
                <li>Ne pas tenter d'accéder à des données ou fonctionnalités non autorisées</li>
                <li>Respecter les droits de propriété intellectuelle de l'Agence MSC - Mon Service Com</li>
                <li>Ne pas utiliser le service à des fins illicites ou frauduleuses</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">Article 7 - Propriété intellectuelle</h2>
              <p>
                L'ensemble des éléments constituant le service ResaWork (logiciel, design, fonctionnalités, 
                documentation) sont la propriété exclusive de l'Agence MSC - Mon Service Com.
              </p>
              <p className="mt-2">
                L'utilisateur bénéficie d'un droit d'usage non exclusif, non transférable et non cessible, 
                limité à la durée de son abonnement.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">Article 8 - Responsabilité</h2>
              <p>
                L'Agence MSC - Mon Service Com s'engage à fournir un service de qualité mais ne peut garantir 
                une disponibilité permanente et ininterrompue du service.
              </p>
              <p className="mt-2">
                L'utilisateur est seul responsable de l'utilisation qu'il fait du service et des données 
                qu'il y enregistre.
              </p>
              <p className="mt-2">
                L'Agence MSC - Mon Service Com ne saurait être tenue responsable des dommages indirects, 
                pertes de données ou manque à gagner.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">Article 9 - Résiliation</h2>
              <ul className="list-disc pl-6 space-y-1">
                <li>L'utilisateur peut résilier son abonnement à tout moment depuis son espace ou en contactant le support</li>
                <li>La résiliation prend effet à la fin de la période d'abonnement en cours</li>
                <li>Aucun remboursement prorata temporis n'est effectué</li>
                <li>L'Agence MSC - Mon Service Com peut résilier le compte en cas de non-respect des CGU</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">Article 10 - Support</h2>
              <p>Le support est accessible :</p>
              <div className="bg-muted/30 p-4 rounded-lg mt-2">
                <ul className="list-none space-y-1">
                  <li><strong>Email :</strong> <a href={`mailto:${RESAWORK_INFO.email}`} className="text-primary hover:underline">{RESAWORK_INFO.email}</a></li>
                  <li><strong>Téléphone :</strong> {RESAWORK_INFO.phone} (Lun-Ven 9h-18h)</li>
                  <li><strong>Adresse :</strong> {RESAWORK_INFO.address}</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">Article 11 - Modification des CGU</h2>
              <p>
                L'Agence MSC - Mon Service Com se réserve le droit de modifier les présentes CGU à tout moment. 
                Les utilisateurs seront informés des modifications par email ou notification dans l'application.
              </p>
              <p className="mt-2">
                La poursuite de l'utilisation du service après notification des modifications vaut acceptation des nouvelles CGU.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">Article 12 - Droit applicable</h2>
              <p>
                Les présentes CGU sont soumises au droit français. Tout litige relatif à l'interprétation 
                ou à l'exécution des présentes sera soumis aux tribunaux compétents d'Aix-en-Provence.
              </p>
            </section>
            
            <p className="text-sm text-muted-foreground">
              Dernière mise à jour : {new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
          </div>
        </div>
      </main>
      
      <LegalFooter />
    </div>
  );
}

// Contact Page
export function ContactPage() {
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <LegalHeader title="Contact" />
      
      <main className="flex-1 pt-20 pb-12">
        <div className="container mx-auto px-4 max-w-4xl">
          <Link to="/" className="inline-flex items-center text-muted-foreground hover:text-foreground mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" /> Retour à l'accueil
          </Link>
          
          <h1 className="text-4xl font-heading font-bold mb-8">Nous contacter</h1>
          
          <div className="grid md:grid-cols-2 gap-8">
            <Card>
              <CardHeader>
                <CardTitle>ResaWork - Support</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Mail className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium">Email</p>
                    <a href={`mailto:${RESAWORK_INFO.email}`} className="text-muted-foreground hover:text-primary transition-colors">
                      {RESAWORK_INFO.email}
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Phone className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium">Téléphone</p>
                    <a href={`tel:${RESAWORK_INFO.phone.replace(/\s/g, '')}`} className="text-muted-foreground hover:text-primary transition-colors">
                      {RESAWORK_INFO.phone}
                    </a>
                    <p className="text-xs text-muted-foreground">Lun-Ven 9h-18h</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium">Adresse</p>
                    <p className="text-muted-foreground">{RESAWORK_INFO.address}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Agence MSC - Mon Service Com</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">
                  ResaWork est un outil développé par l'<strong>Agence MSC - Mon Service Com</strong>, 
                  spécialisée dans le développement de solutions digitales pour les entreprises de services.
                </p>
                <div className="p-4 bg-muted/50 rounded-lg mb-4">
                  <p className="text-sm font-medium mb-2">Siège social :</p>
                  <p className="text-sm text-muted-foreground">{MSC_INFO.address}</p>
                </div>
                <Button asChild variant="outline" className="rounded-full w-full">
                  <a href="https://agence-msc.fr" target="_blank" rel="noopener noreferrer">
                    Visiter agence-msc.fr
                  </a>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
      
      <LegalFooter />
    </div>
  );
}

// Support Page (redirects to tickets)
export function SupportPage() {
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <LegalHeader title="Centre d'aide" />
      
      <main className="flex-1 pt-20 pb-12">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <h1 className="text-4xl font-heading font-bold mb-4">Centre d'aide</h1>
          <p className="text-muted-foreground mb-8">
            Rendez-vous sur notre page de support pour accéder à la FAQ, créer un ticket ou nous contacter.
          </p>
          <Button asChild size="lg" className="rounded-full">
            <Link to="/tickets">Accéder au centre d'aide</Link>
          </Button>
        </div>
      </main>
      
      <LegalFooter />
    </div>
  );
}
